import {get, writable} from 'svelte/store';

import {
    ANALYTICS,
    COOKIE_VALUE,
    STATE,
    VIEW,
    ANIMATION_DURATION,
    POPUP_TYPE,
} from 'general/constants';
import {getCookie} from 'general/libs/getCookie';
import {pushPopupEvent} from 'general/libs/pushDataLayer';
import {setCookie} from 'general/libs/setCookie';
import {openPopup} from 'general/libs/popup';
import {duration} from '../duration';
import type {SelectorsType} from 'types/dy';
import type {PlatformType} from 'types/general';
import {dyData} from 'general/store';
import {CHANGE_VIEW_ANALYTICS} from 'general/store/modal/helpers';
import {expShowPopupSession} from 'general/libs/popup/expShowPopupSession';

export type ModalType = {
    isInitialized: boolean;
    isVisible: boolean;
    isFirstExperience: boolean;
    isExitShowed: boolean;
    state: $Values<typeof STATE>;
    view: $Values<typeof VIEW>;
    email: string;
    refId: string;
    phone: string;
    redirect: string;
    radioValue: string;
    popupType: $Values<typeof POPUP_TYPE>;
    popupSelectors: SelectorsType;
    templateType: string;
    platform?: PlatformType;
    isAltText?: () => boolean;
};

const isPopupCloseBefore = () => Boolean(getCookie(COOKIE_VALUE.POPUP_CLOSE));
const isReminderCloseBefore = () => Boolean(getCookie(COOKIE_VALUE.REMINDER_CLOSE));
const isSubscribedBefore = () => Boolean(getCookie(COOKIE_VALUE.SUBSCRIBER));
const isSubscribedPhoneBefore = () => Boolean(getCookie(COOKIE_VALUE.SUBSCRIBER_PHONE));

const initialValue = {
    // Responsible for the readiness of the widget for rendering
    isInitialized: false,
    // Responsible for the readiness of the widget for display
    isVisible: false,
    // Responsible for the first user experience when a popup appears
    isFirstExperience: true,
    // Responsible for the exit widget was shown
    isExitShowed: false,
    // Current popup type
    popupType: POPUP_TYPE.WELCOME,
    // Current popup template type
    templateType: '',
    // The state of the popup, can have a main state with a subscription form and a reminder with inviting brief information
    state: STATE.MAIN,
    // The view of the popup, can have a main view with a subscription form and a reminder with inviting brief information
    view: VIEW.MAIN,
    // The email that the user entered in the widget
    email: '',
    // Generated id to combine number with mail
    refId: '',
    // The phone number that the user entered in the widget
    phone: '',
    // Follow-up link after subscribing
    redirect: '',
    // Radio input value to send to analytics
    radioValue: '',
    // A function to determine if alternative text should be rendered
    isAltText: () => false,
    // Data selectors for popups
    popupSelectors: {
        welcomePopup: '',
        exitPopup: '',
        triggerPopup: '',
    },
};

const createModalStore = () => {
    const {subscribe, set, update} = writable(<ModalType>{...initialValue});

    /**
     * Defines the status of the popup as ready for rendering
     * @param {boolean} isOpen - open popup immediately ignoring the state of the reminder
     */
    const initialize = (isOpen = false) =>
        update(value => {
            if (isSubscribedBefore() && isSubscribedPhoneBefore()) {
                return value;
            }

            expShowPopupSession(value.popupType);

            if (!(isPopupCloseBefore() && isReminderCloseBefore()) || isOpen) modal.open(isOpen);

            value.isInitialized = true;

            return value;
        });

    /**
     * Switches the popup to a reminder, or completely closes the popup if the user has been subscribed
     */
    const close = () =>
        update(value => {
            window.dispatchEvent(new CustomEvent('mfPopup:close'));

            const isSubscriberPhone = getCookie(COOKIE_VALUE.SUBSCRIBER_PHONE);

            switch (value.state) {
                case STATE.MAIN:
                    pushPopupEvent(ANALYTICS.POPUP_CLOSED);
                    break;
                case STATE.SMS:
                    if (isSubscriberPhone) break;
                    pushPopupEvent(ANALYTICS.POPUP_CLOSED_SMS);
                    break;
            }

            setCookie(COOKIE_VALUE.POPUP_CLOSE, 'true', 1);

            if (
                (getCookie(COOKIE_VALUE.SUBSCRIBER) && isSubscriberPhone) ||
                value.state === STATE.ALREADY_SUBSCRIBED ||
                value.state === STATE.TYP
            ) {
                value.isVisible = false;
            } else {
                changeView(VIEW.REMINDER);

                if (value.popupType === POPUP_TYPE.EXIT) {
                    const timer = setTimeout(() => {
                        value.popupType = POPUP_TYPE.WELCOME;
                        value.isExitShowed = true;
                        openPopup(value.popupSelectors.welcomePopup);
                        clearTimeout(timer);
                    }, ANIMATION_DURATION.ANIMATION_THEME);
                }
            }

            // TODO: GUSA-13495 requires implementation without using CustomEvent
            if (value.email && getCookie(COOKIE_VALUE.SUBSCRIBER)) {
                window.dispatchEvent(
                    new CustomEvent('mfPopups:subscribe', {
                        detail: {
                            payload: {
                                from: 'popup',
                            },
                        },
                    }),
                );
                window.dispatchEvent(
                    new CustomEvent('dynamicYield:newsletter', {
                        detail: {
                            type: 'subscribed',
                            payload: {
                                email: value.email,
                                redirect: value.redirect && location.href,
                                from: 'popup',
                            },
                        },
                    }),
                );
            }

            duration.update('flip', {out: 0});
            duration.update('opacity', {out: 0});

            return value;
        });

    /**
     * Hide current popup
     */
    const hideCurrentPopup = () =>
        update(value => {
            value.isInitialized = false;
            value.isVisible = false;

            return value;
        });

    /**
     * Switches the popup to visibility
     * @param {boolean} isOpen - open popup immediately ignoring the state of the reminder
     */
    const open = (isOpen = false) =>
        update(value => {
            const isSmsSide = Boolean(get(dyData).side?.[STATE.SMS]);

            if (isSubscribedBefore() && !isSubscribedPhoneBefore() && isSmsSide) {
                modal.changeState(STATE.SMS);
            }

            if (isPopupCloseBefore() && !isOpen) {
                changeView(VIEW.REMINDER);
            } else {
                changeView(VIEW.MAIN);
            }

            value.isVisible = true;
            return value;
        });

    /**
     * Toggles the state of the popup. Each popup can have an arbitrary number of sides, the main sides of a popup:
     *    main - the side through which the user can subscribe
     *    subscribed - the side with the user's coupon information, if the user re-entered the email
     *    typ - the side with thanks for subscribing
     * @param {'main' | 'subscribed' | 'typ' | 'sms'} newValue - New state of the popup
     */
    const changeState = (newValue: $Values<typeof STATE>) =>
        update(value => {
            switch (newValue) {
                case STATE.TYP:
                    pushPopupEvent(ANALYTICS.POPUP_SHOWED_TY);
                    break;
                case STATE.ALREADY_SUBSCRIBED:
                    pushPopupEvent(ANALYTICS.POPUP_SHOWED_SUBSCRIBED);
                    break;
            }

            value.state = newValue;

            return value;
        });

    /**
     * Toggles popup view. Each popup has two types of display
     *    main - the main view consisting of several sides
     *    reminder - the small popup window with a reminder about the opportunity to subscribe
     * @param {'main' | 'reminder'} newValue - New popup view
     */
    const changeView = (newValue: $Values<typeof VIEW>) =>
        update(value => {
            const isExitShowed = value.isExitShowed;
            const isReminderDataExists = Boolean(get(dyData).reminder?.[value.state]);

            if (
                !(
                    newValue === VIEW.REMINDER &&
                    (value.popupType === POPUP_TYPE.EXIT || isExitShowed || !isReminderDataExists)
                )
            ) {
                pushPopupEvent(CHANGE_VIEW_ANALYTICS[newValue][value.state]);
            }

            if (isExitShowed) value.isExitShowed = false;

            value.view = newValue;

            return value;
        });

    const setCurrentTemplateType = (templateType: string) => {
        update(value => {
            value.templateType = templateType;
            return value;
        });
    };

    /**
     * Set information about the first user experience
     */
    const setFirstExperience = () =>
        update(value => {
            value.isFirstExperience = false;
            return value;
        });

    /**
     * Sets a function determines if an alternative text should be rendered
     */
    const setIsAltText = (fn: () => boolean) =>
        update(value => {
            value.isAltText = fn;
            return value;
        });

    /**
     * Opens a reminder to the main popup view
     */
    const clickReminder = () =>
        update(value => {
            duration.update('flip', {in: 0, out: 600});

            switch (value.state) {
                case STATE.MAIN:
                    pushPopupEvent(ANALYTICS.REMINDER_CLICKED);
                    break;
                case STATE.SMS:
                    pushPopupEvent(ANALYTICS.REMINDER_CLICKED_SMS);
                    break;
            }

            modal.changeView(VIEW.MAIN);

            return value;
        });

    const handleTriggerPopup = () => {
        update(value => {
            duration.update('flip', {in: 0, out: 600});
            switch (value.state) {
                case STATE.MAIN:
                    pushPopupEvent(ANALYTICS.REMINDER_CLOSED);
                    break;
                case STATE.SMS:
                    pushPopupEvent(ANALYTICS.REMINDER_CLOSED_SMS);
                    break;
            }
            if (getCookie(COOKIE_VALUE.SUBSCRIBER_PHONE)) {
                modal.changeState(STATE.TYP);
                value.view = VIEW.MAIN;
                value.isVisible = true;
            } else {
                modal.changeView(VIEW.MAIN);
            }
            return value;
        });
    };

    /**
     * Close reminder
     */
    const closeReminder = () =>
        update(value => {
            setCookie(COOKIE_VALUE.REMINDER_CLOSE, 'true', 1);

            switch (value.state) {
                case STATE.MAIN:
                    pushPopupEvent(ANALYTICS.REMINDER_CLOSED);
                    break;
                case STATE.SMS:
                    pushPopupEvent(ANALYTICS.REMINDER_CLOSED_SMS);
                    break;
            }

            value.isVisible = false;

            return value;
        });

    /**
     * Update value for email field
     * @param {string} newValue - new value
     */
    const changeEmail = (newValue: string) =>
        update(value => {
            value.email = newValue;
            return value;
        });

    /**
     * Update value for refId field
     * @param {string} newValue - new value
     */
    const changeRefId = (newValue: string) =>
        update(value => {
            value.refId = newValue;
            return value;
        });

    /**
     * Update value for phone field
     * @param {string} newValue - new value
     */
    const changePhone = (newValue: string) =>
        update(value => {
            value.phone = newValue;
            return value;
        });

    /**
     * Update value for radioButton field
     * @param {string} newValue - new value
     */
    const changeRadioValue = (newValue: string) =>
        update(value => {
            value.radioValue = newValue;
            return value;
        });

    /**
     * Update value for redirect
     * @param {string} newValue - new value
     */
    const changeRedirect = (newValue: string) =>
        update(value => {
            value.redirect = newValue;
            return value;
        });

    /**
     * Update value for popupSelectors
     * @param {SelectorsType} newValue - new value
     */
    const changePopupSelectors = (newValue: SelectorsType) => {
        update(value => {
            value.popupSelectors = newValue;
            return value;
        });
    };

    /**
     * Update platform value
     * @param {SelectorsType} newValue - new value
     */
    const changePlatform = (newValue: PlatformType) => {
        update(value => {
            value.platform = newValue;
            return value;
        });
    };

    /**
     * Update value for popupType
     * @param {'welcome' | 'exit'} newValue - new value
     */
    const changePopupType = (newValue: $Values<typeof POPUP_TYPE>) => {
        update(value => {
            value.popupType = newValue;
            return value;
        });
    };

    /**
     * Update value for isExitShowed
     * @param {boolean} newValue - new value
     */
    const changeExitShowed = (newValue: boolean) => {
        update(value => {
            value.isExitShowed = newValue;
            return value;
        });
    };

    return {
        subscribe,
        initialize,
        close,
        open,
        changeState,
        changeView,
        setFirstExperience,
        setCurrentTemplateType,
        setIsAltText,
        clickReminder,
        closeReminder,
        changeEmail,
        changeRefId,
        changePhone,
        changeRedirect,
        changeRadioValue,
        setInitialValue: () => set({...initialValue}),
        handleTriggerPopup,
        hideCurrentPopup,
        changePopupSelectors,
        changePopupType,
        changeExitShowed,
        changePlatform,
    };
};

export const modal = createModalStore();
